import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, CardContent, Card } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { CardGrid } from "components/CardGrid";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { CardHeader } from "components/formFields/formCards/CardHeader";
import { FrmUpsAccount } from "components/formFields/frm/FrmUpsAccount";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { getUpsRates } from "services/sosInventoryService/sosApi";
import { getRecordFrom } from "services/sosInventoryService/sosApi";
import { isValidRates } from "services/sosInventoryService/upsShipment/isValid";
import {
  YOUR_PACKAGING_OPTION,
  STANDARD_CONFIRMATION_OPTION,
  STANDARD_RATE_OPTION,
} from "services/sosInventoryService/upsShipment/schema";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { ConfirmationDialog } from "views/UpsShipments/UpsShipment/Confirmation";
import { FrmServiceCode } from "views/UpsShipments/UpsShipment/FrmServiceCode";
import { PackageInfo } from "views/UpsShipments/UpsShipment/PackageInfo";
import { ShippingAddress } from "views/UpsShipments/UpsShipment/ShippingAddress";
import { ShippingOptions } from "views/UpsShipments/UpsShipment/ShippingOptions";
import { UpsShipmentHeader } from "views/UpsShipments/UpsShipment/UpsShipmentHeader";

import { openAlert } from "globalState/alertSlice";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { INCH_UNIT, LB_UNIT } from "appConstants";

export function UpsShipment(props) {
  const { onClose, objectType, newFromId, newFromObjectType } = props;
  const [record, setRecord, handleInputFieldChange] = useRecord();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [serviceDisabled, setServiceDisabled] = useState(true);

  // UI STATE
  const [rates, setRates] = useState(null);
  const { errors, setErrors, isInError } = useErrors();

  const isLoading = useSelector((state) => state.loading.editModal);

  // OTHER SETUP
  const dispatch = useDispatch();

  useEffect(() => {
    async function _getRecord() {
      if (newFromId) {
        // it's a "populate from" request
        const response = await getRecordFrom(
          objectType,
          newFromObjectType,
          newFromId
        );

        // set defaults and remove id
        response.dimensionUnit = INCH_UNIT;
        response.weightUnit = LB_UNIT;
        response.rateType = STANDARD_RATE_OPTION.id;
        response.packagingCode = YOUR_PACKAGING_OPTION.id;
        response.deliveryConfirmationCode = STANDARD_CONFIRMATION_OPTION.id;
        delete response.id;

        setRecord(response);
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [setRecord, objectType, dispatch, newFromId, newFromObjectType]);

  async function handleRatesLogic(record) {
    // Use the full record for validation
    const errors = isValidRates(record);
    if (errors.messages.length > 0) {
      setServiceDisabled(true);
      return;
    } else {
      setServiceDisabled(false);
      dispatch(editModalLoadingIndicatorOn());
      const { success, data, message } = await getUpsRates(record);
      if (success) {
        setRates(data);
        const rate = data.find(
          ({ serviceCode, saturdayDelivery }) =>
            serviceCode === record.serviceCode &&
            saturdayDelivery === record.saturdayDelivery
        );
        if (record.serviceCode && !rate) {
          // if selected service code does not exist in the new
          //  rates options, then clear the service code selection
          setRecord((prev) => ({
            ...prev,
            serviceCode: null,
            saturdayDelivery: false,
          }));
        }
      } else {
        dispatch(openAlert({ type: "error", message }));
      }
      dispatch(editModalLoadingIndicatorOff());
    }
  }

  function handleScalarChangeAndFetchRates(fieldName, value) {
    handleInputFieldChange(fieldName, value.id);
    handleRatesLogic({ ...record, [fieldName]: value.id });
  }

  async function handleInputChangeAndFetchRates(fieldName, value) {
    handleInputFieldChange(fieldName, value);
    handleRatesLogic({ ...record, [fieldName]: value });
  }

  function handleServiceCodeChange(_, service) {
    setRecord((prevRecord) => ({
      ...prevRecord,
      serviceCode: service.serviceCode,
      saturdayDelivery: service.saturdayDelivery,
    }));
  }

  return (
    <>
      <UpsShipmentHeader
        record={record}
        setErrors={setErrors}
        handleClose={onClose}
        openConfirmDialog={() => setConfirmModalOpen(true)}
      />
      {isLoading && <Loading />}
      <ObjectEditDialogContent>
        <Layout pageTitle={i18n(`objectType.${objectType}.New`)}>
          <Box sx={{ m: 2 }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <ShippingAddress
                    record={record}
                    addressType="fromAddress"
                    residentialAddressType="fromResidentialAddress"
                    title={i18n("frmLabel.ShipFrom")}
                    addressData={record.fromAddress}
                    onValueBlur={handleInputFieldChange}
                    handleInputChangeAndFetchRates={
                      handleInputChangeAndFetchRates
                    }
                    isInError={isInError}
                    setRecord={setRecord}
                    location={record.location}
                  />
                  <ShippingAddress
                    record={record}
                    addressType="toAddress"
                    residentialAddressType="toResidentialAddress"
                    title={i18n("frmLabel.ShipTo")}
                    onValueBlur={handleInputFieldChange}
                    handleInputChangeAndFetchRates={
                      handleInputChangeAndFetchRates
                    }
                    addressData={record.toAddress}
                    isInError={isInError}
                  />
                </FrmFieldGrid>
                <VSpace space={2} />
                <FrmFieldGrid gridAutoFlow="dense">
                  <PackageInfo
                    record={record}
                    onChange={handleInputFieldChange}
                    onChangeAndFetchRates={handleInputChangeAndFetchRates}
                    onScalarChangeAndFetchRates={
                      handleScalarChangeAndFetchRates
                    }
                    isInError={isInError}
                  />
                  <Box>
                    <ShippingOptions
                      record={record}
                      onChange={handleInputFieldChange}
                      onScalarChangeAndFetchRates={
                        handleScalarChangeAndFetchRates
                      }
                      isInError={isInError}
                    />
                    <VSpace space={2} />
                    <Card variant="outlined">
                      <CardContent>
                        <CardHeader text={i18n("frmLabel.Payment")} />
                        <CardGrid>
                          <FrmUpsAccount
                            value={record.upsAccount}
                            onValueChange={handleInputChangeAndFetchRates}
                            error={isInError("upsAccount")}
                          />
                        </CardGrid>
                      </CardContent>
                    </Card>
                    <VSpace space={2} />
                    <FrmServiceCode
                      rates={rates}
                      disabled={serviceDisabled}
                      onValueChange={handleServiceCodeChange}
                      record={record}
                      isInError={isInError}
                    />
                  </Box>
                </FrmFieldGrid>
              </>
            ) : (
              <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
            )}
          </Box>
        </Layout>
      </ObjectEditDialogContent>
      {confirmModalOpen && (
        <ConfirmationDialog
          record={record}
          rates={rates}
          onClose={() => setConfirmModalOpen(false)}
        />
      )}
    </>
  );
}
